import type { ApiErrorBody } from "@theadam/shared";

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

/** Oturum dolunca (auth dışı istekte 401) çağrılır; main.tsx kaydeder. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
    public readonly correlationId?: string,
    public readonly helpUrl?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown, file?: Blob, opts: { timeoutMs?: number } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (file) headers["Content-Type"] = file.type || "application/octet-stream";
  else if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  // Yanıt gelmeyen istek sonsuza dek beklemesin (ekran sessizce donmasın): okuma 30 sn, yazma 90 sn.
  const timeoutMs = opts.timeoutMs ?? (method === "GET" ? 30_000 : 90_000);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: file ?? (body === undefined ? undefined : JSON.stringify(body)),
      credentials: "same-origin",
      signal: controller.signal,
    });
  } catch {
    if (controller.signal.aborted) throw new ApiError(0, "timeout", "Sunucu yanıt vermedi (zaman aşımı). Biraz sonra tekrar deneyin.");
    throw new ApiError(0, "network_error", "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.");
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let data: unknown;
  try {
    data = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    data = undefined;
  }
  if (res.status === 413) throw new ApiError(413, "too_large", "Dosya çok büyük (en fazla 10 MB)");
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/")) onUnauthorized?.();
    const err = (data as ApiErrorBody | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "http_error", err?.message ?? `İstek başarısız (${res.status})`, err?.fields, err?.correlationId, err?.helpUrl);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: { timeoutMs?: number }) => request<T>("GET", path, undefined, undefined, opts),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  /** Ham dosya gövdesiyle yükleme (ör. fatura PDF'i). */
  upload: <T>(path: string, file: Blob) => request<T>("POST", path, undefined, file),
};
